<main>
  <loading-overlay [isLoading]="isLoading"></loading-overlay>

  <!-- Header -->
  <header>
    <div>
      <div class="flex gap-[2rem] items-center">
        @if (isCreateMode()) {
          <h1>{{ 'New_Requisition' | translateWF: 'New Production Requisition' }}</h1>
        } @else {
          <h1>{{ 'Requisition_Details' | translateWF: 'Requisition' }}: {{ requisition()?.requisitionNumber }}</h1>
          <csl-dropdown [formControl]="statusControl" [enumObject]="statusEnumObject" [enumValueOptions]="validStatusOptions" [disabled]="!canEdit()" />
        }
      </div>
    </div>
    <div class="buttons">
      @if (isCreateMode() && canCreate() && canEdit()) {
        <p-button [label]="'Create' | translateWF: 'Create'" icon="pi pi-check" [loading]="isLoading" (click)="save()" />
      }

      <p-button [label]="'Back' | translateWF: 'Back'" icon="pi pi-arrow-left" variant="outlined" (click)="goBack()" />
    </div>
  </header>

  <div class="content">
    <!-- SECTION 1: Requisition Info (edit mode) -->
    @if (!isCreateMode() && requisition()) {
      <p-panel [header]="'Requisition_Info' | translateWF: 'Requisition Info'">
        <p-card>
          <div class="grid-12">
            <div class="span-12-3">
              <label class="font-semibold text-gray-600">{{ 'Created_Date' | translateWF: 'Created Date' }}</label>
              <p class="mt-1">{{ requisition()!.createdDate | date: 'dd/MM/yyyy HH:mm' }}</p>
            </div>
          </div>
        </p-card>
      </p-panel>
    }

    <!-- SECTION 2: PWO Search (create mode, before PWO selected) -->
    @if (isCreateMode() && !productionWorkOrder()) {
      <p-panel [header]="'Select_PWO' | translateWF: 'Select Production Work Order'">
        <p-card>
          <div class="grid-12">
            <div class="span-12-6">
              <label class="font-semibold text-gray-600 mb-2 block">
                {{ 'Search_PWO' | translateWF: 'Search by PWO number' }}
              </label>
              <p-autoComplete
                [formControl]="pwoSearchControl"
                #pwoSearchRef="cslServerSearch"
                [cslServerSearch]="fetchPWOs"
                [suggestions]="pwoSearchRef.results()"
                (onSelect)="onPWOSelected($event)"
                field="number"
                [placeholder]="'Enter_PWO_Number' | translateWF: 'Enter PWO number...'"
                styleClass="w-full"
              />
              <small class="text-gray-500 mt-1 block">
                {{ 'PWO_Status_Hint' | translateWF: 'Only Production Work Orders in these statuses are shown:' }}
                <span class="font-medium">{{ validPwoStatusHint() }}</span>
              </small>
            </div>
          </div>
        </p-card>
      </p-panel>
    }

    <!-- SECTION 3: Batches Table (create mode, after PWO selected) -->
    @if (isCreateMode() && productionWorkOrder()) {
      <p-panel>
        <ng-template pTemplate="header">
          <div class="col">
            <span>{{ 'PWO' | translateWF: 'PWO' }}: {{ productionWorkOrder()!.number }} — {{ 'Select_Batch' | translateWF: 'Select Batch' }}</span>
            <small class="text-gray-500 mt-1 block">
              {{ 'Batch_Status_Hint' | translateWF: 'Only batches in these statuses are eligible:' }}
              <span class="font-medium">{{ validBatchStatusHint() }}</span>
            </small>
          </div>
        </ng-template>
        <p-card data-variant="compact">
          <p-table [value]="batches()" selectionMode="single" styleClass="p-datatable-sm">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ 'Batch_Number' | translateWF: 'Batch Number' }}</th>
                <th>{{ 'Recipe' | translateWF: 'Recipe' }}</th>
                <th>{{ 'Status' | translateWF: 'Status' }}</th>
                <th>{{ 'Planned_Qty' | translateWF: 'Planned Qty' }}</th>
                <th>{{ 'UOM' | translateWF: 'UOM' }}</th>
                <th></th>
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-batch>
              <tr [class]="selectedBatch()?.id === batch.id ? 'bg-blue-50' : ''">
                <td>{{ batch.number }}</td>
                <td>{{ batch.productDevelopmentNumber }}</td>
                <td>{{ batch.status }}</td>
                <td>{{ batch.plannedQuantity }}</td>
                <td>{{ unitOfMeasures[batch.uom] || batch.uom }}</td>
                <td>
                  <p-button
                    [label]="selectedBatch()?.id === batch.id ? ('Selected' | translateWF: 'Selected') : ('Select' | translateWF: 'Select')"
                    [icon]="selectedBatch()?.id === batch.id ? 'pi pi-check' : 'pi pi-arrow-right'"
                    size="small"
                    [severity]="selectedBatch()?.id === batch.id ? 'success' : 'info'"
                    (click)="selectBatch(batch)"
                  />
                </td>
              </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="6">{{ 'No_Batches' | translateWF: 'No batches found for this work order.' }}</td>
              </tr>
            </ng-template>
          </p-table>
        </p-card>
      </p-panel>
    }

    <!-- SECTION 4: Batch & PWO Info (edit mode) -->
    @if (!isCreateMode() && selectedBatch()) {
      <p-panel>
        <ng-template pTemplate="header"> {{ 'Batch_Info' | translateWF: 'Batch' }}: {{ selectedBatch()!.number }} </ng-template>
        <p-card>
          <div class="grid-12 mb-4">
            <div class="span-12-3">
              <label class="font-semibold text-gray-600">{{ 'Recipe' | translateWF: 'Recipe' }}</label>
              <p class="mt-1">{{ selectedBatch()!.productDevelopmentNumber }}</p>
              @if (selectedBatchRecipe()?.name) {
                <p class="text-xs text-gray-500">{{ selectedBatchRecipe()!.name }}</p>
              }
            </div>
            <div class="span-12-3">
              <label class="font-semibold text-gray-600">{{ 'Batch_Status' | translateWF: 'Batch Status' }}</label>
              <p class="mt-1">{{ selectedBatch()!.status }}</p>
            </div>
            <div class="span-12-3">
              <label class="font-semibold text-gray-600">{{ 'Planned_Qty' | translateWF: 'Planned Qty' }}</label>
              <p class="mt-1">{{ selectedBatch()!.plannedQuantity }}</p>
            </div>
            @if (productionWorkOrder()) {
              <div class="span-12-3">
                <label class="font-semibold text-gray-600">{{ 'Production_Work_Order' | translateWF: 'PWO' }}</label>
                <p class="mt-1">{{ productionWorkOrder()!.number }}</p>
              </div>
            }
          </div>
        </p-card>
      </p-panel>
    }

    <!-- SECTION 5: Available Ingredients (only when status is Requested) -->
    @if (!isCreateMode() && showSaveButton() && ingredientNodes().length > 0) {
      <p-panel [header]="'Available_Ingredients' | translateWF: 'Available Ingredients'">
        <ng-template pTemplate="icons">
          @if (canEdit()) {
            <p-button
              icon="pi pi-plus-circle"
              [label]="'Add_All' | translateWF: 'Add All Ingredients'"
              size="small"
              severity="secondary"
              (click)="addAllToEntries()"
              [disabled]="allIngredientsAdded()"
            />
          }
        </ng-template>
        <div class="col-xs">
          <p-card data-variant="compact">
            <p-table [value]="ingredientNodes()" [scrollable]="true" styleClass="p-datatable-sm">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ 'Item_Code' | translateWF: 'Item Code' }}</th>
                  <th>{{ 'Name' | translateWF: 'Name' }}</th>
                  <th>{{ 'Recipe' | translateWF: 'Recipe' }}</th>
                  <th pTooltip="{{ 'Recipe_Qty_Base_Tooltip' | translateWF: 'Quantity per recipe base output' }}" tooltipPosition="top">
                    {{ 'Recipe_Qty_Base' | translateWF: 'Recipe Qty (Base)' }}
                  </th>
                  <th pTooltip="{{ 'Expected_Qty_Tooltip' | translateWF: 'Scaled quantity based on batch planned quantity' }}" tooltipPosition="top">
                    {{ 'Expected_Qty' | translateWF: 'Expected' }}
                  </th>
                  <th>{{ 'UOM' | translateWF: 'UOM' }}</th>
                  <th>{{ 'For_Product' | translateWF: 'For Product' }}</th>
                  <th></th>
                </tr>
              </ng-template>

              <ng-template pTemplate="body" let-node>
                <tr [class]="node.type === 'substitute' ? 'bg-gray-50' : ''">
                  <td [class]="node.type === 'substitute' ? 'pl-8' : ''">
                    @if (node.type === 'substitute') {
                      <i class="pi pi-arrow-right mr-2 text-gray-400 text-xs"></i>
                    }
                    @if (node.isRecipeIngredient) {
                      <i class="pi pi-sitemap mr-2 text-purple-500 text-xs" pTooltip="{{ 'Sub_Recipe_Ingredient' | translateWF: 'Sub-recipe ingredient' }}"></i>
                    }
                    {{ node.itemCode || node.productRecipeNumber }}
                  </td>
                  <td>
                    {{ node.itemDisplayName }}
                    @if (node.isRecipeIngredient && node.subRecipeName) {
                      <div class="text-xs text-gray-500">{{ node.subRecipeName }}</div>
                    }
                    @if (node.outputItemCodeMissing) {
                      <div class="text-xs text-amber-700 row-start-center gap-1 mt-1">
                        <i class="pi pi-exclamation-triangle"></i>
                        {{
                          'Recipe_Missing_Output_Item_Master'
                            | translateWF
                              : 'Recipe has no OutputItemCode. Link an item
                  master before requisitioning.'
                        }}
                      </div>
                    }
                  </td>
                  <td>{{ node.recipeName }}</td>
                  <td>{{ node.quantity | number: '1.2-4' }}</td>
                  <td class="font-semibold">{{ node.expectedQuantity | number: '1.2-4' }}</td>
                  <td>{{ unitOfMeasures[node.uom] || node.uom }}</td>
                  <td>{{ getItemName(node.recipeOutputItemCode) }}</td>
                  <td>
                    @if (canEdit() && node.itemCode && !node.outputItemCodeMissing) {
                      <p-button icon="pi pi-plus" [label]="'Add' | translateWF: 'Add'" size="small" (click)="addToEntries(node)" [disabled]="isEntryAlreadyAdded(node)" />
                    } @else if (canEdit() && node.outputItemCodeMissing) {
                      <p-button
                        icon="pi pi-exclamation-triangle"
                        [label]="'Add' | translateWF: 'Add'"
                        size="small"
                        severity="warn"
                        [disabled]="true"
                        pTooltip="{{ 'Cannot_Add_Recipe_Without_Output' | translateWF: 'Cannot add: recipe has no OutputItemCode linked to an item master.' }}"
                      />
                    }
                  </td>
                </tr>
              </ng-template>

              <ng-template pTemplate="emptymessage">
                <tr>
                  <td colspan="8">{{ 'No_Ingredients' | translateWF: 'No ingredients found in selected recipes.' }}</td>
                </tr>
              </ng-template>
            </p-table>
          </p-card>
          @if (canEdit()) {
            <p-card>
              <h3 class="mb-[0.75rem]">{{ 'Additional_Items' | translateWF: 'Additional Items' }}</h3>
              <p class="text-sm text-gray-500 mb-[0.75rem]">
                {{ 'Additional_Items_Hint' | translateWF: 'Items outside the recipe. Only raw materials available for this site can be requested.' }}
              </p>
              <div class="row-start-center gap-[0.75rem]">
                <p-select
                  [formControl]="additionalItemControl"
                  [options]="availableAdditionalItems()"
                  optionLabel="itemCode"
                  [filter]="true"
                  filterBy="itemCode,itemLongDisplayName"
                  [showClear]="true"
                  [placeholder]="'Select_Additional_Item' | translateWF: 'Select an item to add'"
                  styleClass="w-[28rem]"
                >
                  <ng-template let-item pTemplate="item">
                    <span>{{ item.itemCode }} — {{ getItemDisplayName(item) }}</span>
                  </ng-template>
                </p-select>
                <p-button icon="pi pi-plus" [label]="'Add' | translateWF: 'Add'" size="small" (click)="addAdditionalItemToEntries()" [disabled]="!additionalItemControl.value" />
              </div>
            </p-card>
          }
        </div>
      </p-panel>
    }

    <!-- SECTION 6: Requisition Entries (only after requisition is created) -->
    @if (!isCreateMode()) {
      <p-panel [header]="'Requisition_Entries' | translateWF: 'Requisition Entries'">
        <ng-template pTemplate="icons">
          @if (canEdit() && entriesFormArray.length > 0 && showSaveButton()) {
            <p-button [label]="'Save_Entries' | translateWF: 'Save Entries'" icon="pi pi-save" size="small" [loading]="isLoading" (click)="patchEntries()" />
          }
        </ng-template>

        @if (entriesFormArray.length === 0) {
          <p-card>
            <p class="text-gray-500">
              {{ 'No_Entries' | translateWF: 'No entries added yet. Select ingredients from the list above.' }}
            </p>
          </p-card>
        } @else {
          <p-card data-variant="compact">
            <p-table [value]="entriesFormArray.controls" styleClass="p-datatable-sm">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ 'Item_Code' | translateWF: 'Item Code' }}</th>
                  <th>{{ 'Name' | translateWF: 'Name' }}</th>
                  <th>{{ 'Material_For' | translateWF: 'Material For' }}</th>
                  <th>{{ 'UOM' | translateWF: 'UOM' }}</th>
                  <th style="width: 150px">{{ 'Quantity' | translateWF: 'Quantity' }}</th>
                  <th style="width: 200px">{{ 'Manufacturer' | translateWF: 'Manufacturer' }}</th>
                  <th style="width: 150px">{{ 'Lot_Number' | translateWF: 'Lot Number' }}</th>
                  <th style="width: 60px"></th>
                </tr>
              </ng-template>

              <ng-template pTemplate="body" let-group let-index="rowIndex">
                <tr>
                  <td>{{ group.get('itemCode').value }}</td>
                  <td>{{ getItemName(group.get('itemCode').value) }}</td>
                  <td>
                    @if (group.get('materialForItemCode').value) {
                      {{ getItemName(group.get('materialForItemCode').value) }}
                    } @else {
                      <p-tag severity="secondary" [value]="'Additional_Item' | translateWF: 'Additional item'" />
                    }
                  </td>
                  <td>{{ unitOfMeasures[group.get('unitOfMeasure').value] || group.get('unitOfMeasure').value }}</td>
                  <td>
                    @if (showSaveButton()) {
                      <p-inputNumber
                        [formControl]="group.get('requiredQuantityInUnitOfMeasure')"
                        [minFractionDigits]="2"
                        [maxFractionDigits]="4"
                        [min]="0.01"
                        mode="decimal"
                        styleClass="w-full"
                        [placeholder]="'Enter_Qty' | translateWF: 'Qty'"
                      />
                    } @else {
                      {{ group.get('requiredQuantityInUnitOfMeasure').value | number: '1.2-4' }}
                    }
                  </td>
                  <td>
                    @if (showSaveButton()) {
                      <p-autoComplete
                        appendTo="body"
                        [formControl]="group.get('manufacturer')"
                        #manufacturerSearchRef="cslServerSearch"
                        [cslServerSearch]="fetchManufacturers"
                        [suggestions]="manufacturerSearchRef.results()"
                        field="name"
                        [placeholder]="'Search_Manufacturer' | translateWF: 'Search...'"
                        styleClass="w-full"
                      />
                    } @else {
                      {{ group.get('manufacturer')?.value?.name }}
                    }
                  </td>
                  <td>
                    @if (showSaveButton()) {
                      <p-select
                        appendTo="body"
                        [formControl]="group.get('providerLotNumber')"
                        [options]="group.get('manufacturer')?.value?.lotNumbers || []"
                        [placeholder]="'Select_Lot' | translateWF: 'Select lot #'"
                        [disabled]="!group.get('manufacturer')?.value"
                        [filter]="true"
                        [showClear]="true"
                        [filterPlaceholder]="'Search_Lot' | translateWF: 'Search lot #...'"
                        styleClass="w-full"
                      />
                    } @else {
                      {{ group.get('providerLotNumber').value }}
                    }
                  </td>
                  <td>
                    @if (canEdit() && showSaveButton()) {
                      <p-button icon="pi pi-trash" severity="danger" variant="text" size="small" (click)="removeEntry(index)" />
                    }
                  </td>
                </tr>
              </ng-template>

              <ng-template pTemplate="footer">
                <tr>
                  <td colspan="4" class="text-right font-bold">{{ 'Total' | translateWF: 'Total' }}:</td>
                  <td class="font-bold">{{ entriesTotalQuantity() | number: '1.2-4' }}</td>
                  <td colspan="3"></td>
                </tr>
              </ng-template>
            </p-table>
          </p-card>
        }
      </p-panel>
    }

    <!-- SECTION 7: Lot Items (visible after creation, add/remove only in Processing) -->
    @if (!isCreateMode()) {
      <p-panel [header]="'Lot_Items' | translateWF: 'Lot Items'">
        <div class="col-xs">
          <!-- Search bar: only when Processing -->
          @if (isProcessing() && canEdit()) {
            <p-card>
              <div class="grid-auto-3">
                <div class="form-box">
                  <label class="font-semibold text-gray-600 mb-2 block">{{ 'Target_Item' | translateWF: 'Target Item Code' }}</label>
                  <p-select
                    [formControl]="lotItemTargetItemCode"
                    [options]="entryItemCodeOptions()"
                    optionLabel="label"
                    optionValue="value"
                    [placeholder]="'Select_Entry' | translateWF: 'Select entry item...'"
                    styleClass="w-full"
                  />
                </div>
                <div class="form-box">
                  <label class="font-semibold text-gray-600 mb-2 block">{{
                    'Search_Lot_Item'
                      | translateWF
                        : 'Search Lot
                Item'
                  }}</label>
                  <p-autoComplete
                    fluid
                    appendTo="body"
                    [formControl]="lotItemSearchControl"
                    #lotItemSearchRef="cslServerSearch"
                    [cslServerSearch]="fetchLotItems"
                    [suggestions]="lotItemSearchRef.results()"
                    (onSelect)="onLotItemSelected($event)"
                    field="number"
                    [disabled]="!lotItemTargetItemCode.value"
                    [placeholder]="'Enter_Lot_Number' | translateWF: 'Search by lot item number...'"
                    class="w-full"
                    styleClass="w-full"
                  />
                </div>
              </div>
            </p-card>
          }
          <!-- Grouped lot items per entry -->
          @for (group of entriesFormArray.controls; track $index) {
            @let itemCode = group.get('itemCode')?.value || '';
            @let lotItems = getLotItemsForEntry(itemCode);
            @if (lotItems.length > 0) {
              <p-panel [header]="itemCode + ' - ' + getItemName(itemCode)" data-tone="surface">
                <p-card data-variant="compact">
                  <p-table [value]="lotItems" styleClass="p-datatable-sm">
                    <ng-template pTemplate="header">
                      <tr>
                        <th>{{ 'Lot_Item_Number' | translateWF: 'Lot Item #' }}</th>
                        <th pTooltip="{{ 'Qty_At_Transaction_Time' | translateWF: 'Quantity at the time of transaction' }}" tooltipPosition="top">
                          {{ 'Quantity' | translateWF: 'Quantity' }}
                        </th>
                        <th>{{ 'Location' | translateWF: 'Location' }}</th>
                        @if (isProcessing()) {
                          <th style="width: 60px"></th>
                        }
                      </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-assigned>
                      <tr>
                        <td>{{ assigned.lotItem.number }}</td>
                        <td>{{ assigned.transactionQuantity }}</td>
                        <td>{{ assigned.lotItem.currentLocationCode }}</td>
                        @if (isProcessing()) {
                          <td>
                            @if (canEdit()) {
                              <p-button icon="pi pi-trash" severity="danger" variant="text" size="small" (click)="removeLotItem(assigned.lotItem)" />
                            }
                          </td>
                        }
                      </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                      <tr>
                        <td [attr.colspan]="isProcessing() ? 4 : 3">{{ 'No_Lot_Items' | translateWF: 'No lot items assigned.' }}</td>
                      </tr>
                    </ng-template>
                  </p-table>
                </p-card>
              </p-panel>
            }
          }
        </div>
      </p-panel>
    }
  </div>
</main>

<p-confirmDialog />
